import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { useLocale } from "../Providers/LocaleContext";
import { useMenu } from "./MenuContext";
import getMenuItems from "../../config/menuItems";
import Footer from "components/Common/Footer";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";

const Menu: React.FC<any> = (props) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const menuContext = useMenu();
  const { toggleThis, isDesktop } = menuContext || {};
  const { setLocale, locale = "en" } = useLocale();

  const menuItems = getMenuItems({
    intl,
    locale,
    updateLocale: setLocale,
    menuContext,
    ...props,
  }).filter((item: any) => {
    return item.visible !== false;
  });

  const handleItemClick = (item: any) => {
    // On mobile screens, auto-close the drawer when an item is selected
    if (!isDesktop && toggleThis) {
      toggleThis("isMenuOpen", false);
    }

    if (item.onClick) {
      item.onClick();
    } else if (item.value) {
      navigate(item.value, { replace: true });
    }
  };

  const isItemActive = (itemValue?: string) => {
    if (!itemValue) return false;
    if (itemValue === "/home") {
      return location.pathname === "/home" || location.pathname === "/";
    }
    return location.pathname.startsWith(itemValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
        justifyContent: "space-between",
      }}
    >
      <List sx={{ px: 1.5, py: 1.5, width: "100%" }}>
        {menuItems.map((item: any, index: number) => {
          if (item.divider === true) {
            return (
              <Divider
                key={`divider-${index}`}
                sx={{
                  my: 1.5,
                  borderColor: "divider",
                  opacity: 0.6,
                }}
              />
            );
          }

          const active = isItemActive(item.value);
          const isLogout = item.value === "/Logout";

          return (
            <ListItem
              key={`menu-${item.value || index}`}
              disablePadding
              sx={{ mb: 0.75 }}
            >
              <ListItemButton
                onClick={() => handleItemClick(item)}
                sx={{
                  borderRadius: "10px",
                  py: 1,
                  px: 1.75,
                  transition: "all 0.2s ease-in-out",
                  bgcolor: active
                    ? "primary.main"
                    : isLogout
                    ? "transparent"
                    : "transparent",
                  color: active
                    ? "primary.contrastText"
                    : isLogout
                    ? "error.main"
                    : "text.primary",
                  boxShadow: active
                    ? "0 4px 10px rgba(54, 135, 39, 0.25)"
                    : "none",
                  "&:hover": {
                    bgcolor: active
                      ? "primary.dark"
                      : isLogout
                      ? "error.light"
                      : "action.hover",
                    color: active
                      ? "primary.contrastText"
                      : isLogout
                      ? "error.contrastText"
                      : "primary.main",
                    transform: "translateX(3px)",
                    "& .MuiListItemIcon-root": {
                      color: active
                        ? "inherit"
                        : isLogout
                        ? "error.contrastText"
                        : "primary.main",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 38,
                    color: active
                      ? "primary.contrastText"
                      : isLogout
                      ? "error.main"
                      : "text.secondary",
                    transition: "color 0.2s ease-in-out",
                  }}
                >
                  {item.leftIcon}
                </ListItemIcon>
                <ListItemText
                  primary={item.primaryText}
                  primaryTypographyProps={{
                    fontWeight: active ? 600 : 500,
                    fontSize: "0.92rem",
                    letterSpacing: 0.2,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ pb: 1, px: 1 }}>
        <Footer copyright={true} />
      </Box>
    </Box>
  );
};

export default Menu;
